import { debounce, isArray, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { PROJECT_AREA_LINE_COLOR } from '~/colors';
import { GeoItemMarkerDot } from '~/components2/Map/GeoItemMarkerDot';
import { mapState as MapViewMapState } from '~/components2/Map/Map';
import { useSiteDataLegendSection } from '~/pages/projectDetails/map/useSiteDataLegendSection';
import layerStore from '~/store2/layers';
import Brand from './Brand';

const rowStyle = 'my-1 flex flex-row items-center space-x-4';

export default function PrintViewLegend() {
  const { tilesConfig } = useSnapshot(layerStore) as typeof layerStore;
  const mapState = useSnapshot(MapViewMapState) as typeof MapViewMapState;
  const { current: map } = useMap();
  const [currentlyVisibleLayerIds, setCurrentlyVisibleLayerIds] = useState<string[]>([]);
  const siteDataSection = useSiteDataLegendSection();

  function _refreshLegendList() {
    if (map?.isStyleLoaded()) {
      const layersInView = map.queryRenderedFeatures();
      const uniqueSourceIds = [...new Set(layersInView.map((f) => f.properties?.cgSourceId))].filter(
        (id) => id !== undefined,
      ) as string[];

      // get all visible raster layers and assume they are currently displayed on the screen
      // this is hack until Mapbox fixes https://github.com/mapbox/mapbox-gl-js/issues/1404
      const rasterLayerIds = map
        .getStyle()
        .layers.filter((l) => l.type === 'raster' && l.id !== 'satellite')
        .map((l) => l.id);

      setCurrentlyVisibleLayerIds([...uniqueSourceIds, ...rasterLayerIds]);
    } else {
      return [];
    }
  }

  const refreshLegendList = debounce(_refreshLegendList, 250);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    refreshLegendList();
  }, [mapState]);

  const els = useMemo(() => {
    const list = isEmpty(currentlyVisibleLayerIds)
      ? []
      : currentlyVisibleLayerIds.flatMap((l) => {
          // TODO(CG-668): tilesConfig can still be loading when a persisted-visible layer hits currentlyVisibleLayerIds.
          const config = tilesConfig?.[l];
          if (!config) return [];
          const items = config.legend.map((l) => (
            <div key={l.label} className={rowStyle}>
              <img src={`data:image/jpeg;base64,${l.imageData}`} width={l.width} height={l.height} alt="" />

              <div className="">{l.label}</div>
            </div>
          ));
          return (
            <div key={l}>
              <div className="font-bold">{config.name}</div>
              {items}
            </div>
          );
        });

    // Site Data draws as client GeoJSON with no tilesConfig entry, so the lookup above can't
    // see it. Same rows as the on-map legend.
    if (siteDataSection) {
      list.push(
        <div key="site-data" data-testid="print-legend-site-data-section">
          <div className="font-bold">{siteDataSection.label}</div>
          {siteDataSection.items.map((item) => (
            <div key={item.key} className={rowStyle} data-testid={`print-legend-site-data-item-${item.key}`}>
              <GeoItemMarkerDot fill={item.fill} stroke={item.stroke} dotSize={14} />
              <div className="">{item.label}</div>
            </div>
          ))}
        </div>,
      );
    }

    if (isEmpty(list)) {
      return <div className="print:hidden">Zoom in to see data</div>;
    }

    return list;
  }, [currentlyVisibleLayerIds, siteDataSection, tilesConfig]);

  const getTextSize = () => {
    let textSize = 'text-2xs';

    if (isArray(els)) {
      // Thresholds were tuned for tile sections alone, so the Site Data group must not shrink them.
      const tileSectionCount = els.length - (siteDataSection ? 1 : 0);
      if (tileSectionCount === 5) {
        textSize = 'text-[9px]';
      } else if (tileSectionCount > 5) {
        textSize = 'text-[7px]';
      }
    }

    return textSize;
  };

  return (
    <div
      id="print-view-legend"
      className="overflow-y-none absolute bottom-0 left-0 z-[10000] flex h-[30vh] w-screen flex-col rounded bg-white p-1 shadow-sm"
    >
      <div key="project">
        <div className={rowStyle}>
          <div style={{ border: `3px dashed ${PROJECT_AREA_LINE_COLOR}`, width: 20, height: 20 }} />
          <div className="text-xs">Project Area</div>
        </div>
      </div>
      <div className={`flex gap-2 ${getTextSize()}`}>{els}</div>
      <Brand />
    </div>
  );
}
